import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { ConciergeBell, Plus, Trash2, Loader2, Settings, UserCheck, AlertTriangle, BarChart3 } from "lucide-react";

const TEMA_VACIO = { nombre: "", descripcion: "", department_id: "", department_name: "", assigned_user_id: "", assigned_user_name: "", preguntas: [] };

const FORM_DEFAULT = {
  config_id: "",
  is_active: true,
  pregunta_inicial: "¿En qué podemos ayudarle hoy? Cuéntenos brevemente lo que necesita.",
  temas: [],
  max_reintentos: 2,
  reactivar_horas: 24,
  mensaje_despedida: "Perfecto ✅ Le comunicamos con {departamento}. En breve un agente le atenderá con su información."
};

export const WhatsAppRecepcionistaSection = ({ configs, departamentos, isAdmin, getAuthHeaders }) => {
  const [recepConfigs, setRecepConfigs] = useState([]);
  const [ivrConfigs, setIvrConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(FORM_DEFAULT);
  const [usuarios, setUsuarios] = useState([]);
  const [statsConfig, setStatsConfig] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsDays, setStatsDays] = useState(30);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users`, getAuthHeaders())
      .then((res) => setUsuarios((res.data || []).filter((u) => u.is_active !== false)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/whatsapp/recepcionista/config`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/ivr/config`, getAuthHeaders())
      ]);
      setRecepConfigs(r1.data || []);
      setIvrConfigs(r2.data || []);
    } catch (err) {
      toast.error("Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deptName = (id) => {
    const d = departamentos.find((x) => x.id === id);
    return d ? (d.name || d.nombre) : "";
  };

  const userName = (u) => u.name || u.email;

  const setTema = (idx, cambios) => {
    setForm((f) => ({ ...f, temas: f.temas.map((t, i) => (i === idx ? { ...t, ...cambios } : t)) }));
  };

  const openEditor = (config) => {
    const existente = recepConfigs.find((r) => r.config_id === config.id);
    setForm(existente ? { ...FORM_DEFAULT, ...existente } : { ...FORM_DEFAULT, config_id: config.id });
    setShowDialog(true);
  };

  const fetchStats = async (configId, days) => {
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/whatsapp/recepcionista/stats?config_id=${configId}&days=${days}`, getAuthHeaders());
      setStatsData(res.data);
    } catch (err) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoadingStats(false);
    }
  };

  const openStats = (config) => {
    setStatsConfig(config);
    setStatsData(null);
    fetchStats(config.id, statsDays);
  };

  const handleGuardar = async () => {
    if (form.temas.length === 0) {
      toast.error("Agregue al menos un tema de atención");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/whatsapp/recepcionista/config`, {
        ...form,
        max_reintentos: parseInt(form.max_reintentos, 10) || 2,
        reactivar_horas: parseInt(form.reactivar_horas, 10) || 24
      }, getAuthHeaders());
      toast.success("Bot Recepcionista guardado");
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (recep, activo) => {
    try {
      await axios.post(`${API}/whatsapp/recepcionista/config`, { ...recep, is_active: activo }, getAuthHeaders());
      toast.success(activo ? "Recepcionista activado" : "Recepcionista desactivado");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleEliminar = async (recep) => {
    if (!window.confirm("¿Eliminar el Bot Recepcionista de esta línea?")) return;
    try {
      await axios.delete(`${API}/whatsapp/recepcionista/config/${recep.config_id}`, getAuthHeaders());
      toast.success("Bot Recepcionista eliminado");
      fetchAll();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="recepcionista-section">
      <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-900/20 dark:to-cyan-900/20">
        <CardContent className="pt-5 pb-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center shrink-0">
            <ConciergeBell className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <p className="font-semibold text-sky-900 dark:text-sky-100">Bot Recepcionista</p>
            <p className="text-sm text-sky-700 dark:text-sky-300">
              Saluda según la hora del día, pregunta qué necesita el cliente, la IA identifica el tema y el bot hace las
              preguntas configuradas. Al pasar la conversación al ejecutivo, este recibe un resumen 📋 con toda la información.
              Si esta línea también tiene IVR activo, el Recepcionista atiende primero.
            </p>
          </div>
        </CardContent>
      </Card>

      {configs.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-slate-500">Primero configura una línea de WhatsApp</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {configs.map((config) => {
            const recep = recepConfigs.find((r) => r.config_id === config.id);
            const ivrActivo = ivrConfigs.some((i) => i.config_id === config.id && i.is_active);
            return (
              <Card key={config.id} data-testid={`recep-card-${config.id}`} className={recep?.is_active ? "border-sky-200" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ConciergeBell className="w-4 h-4 text-sky-600" />
                      {config.name || config.phone_number}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {recep && (
                        <Badge variant={recep.is_active ? "default" : "secondary"} className={recep.is_active ? "bg-sky-500" : ""} data-testid={`recep-estado-${config.id}`}>
                          {recep.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                      )}
                      {recep && isAdmin && (
                        <Switch checked={!!recep.is_active} onCheckedChange={(v) => handleToggle(recep, v)} data-testid={`recep-toggle-${config.id}`} />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recep?.is_active && ivrActivo && (
                    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2" data-testid={`recep-aviso-ivr-${config.id}`}>
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      El IVR de esta línea también está activo: el Recepcionista atenderá primero y el IVR no se mostrará.
                    </div>
                  )}
                  {recep ? (
                    <div className="space-y-1">
                      {(recep.temas || []).map((t, i) => (
                        <div key={i} className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
                          <span className="text-sky-600">•</span>
                          <span>{t.nombre}</span>
                          <span className="text-xs text-slate-400">→ {t.department_name || deptName(t.department_id)}{(t.preguntas || []).length > 0 ? ` · ${t.preguntas.length} pregunta(s)` : ""}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Sin Bot Recepcionista configurado para esta línea.</p>
                  )}
                  {isAdmin && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditor(config)} data-testid={`recep-configurar-${config.id}`}>
                        <Settings className="w-4 h-4 mr-2" />{recep ? "Editar" : "Crear Recepcionista"}
                      </Button>
                      {recep && (
                        <Button variant="outline" size="sm" onClick={() => openStats(config)} data-testid={`recep-stats-${config.id}`}>
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                      )}
                      {recep && (
                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleEliminar(recep)} data-testid={`recep-eliminar-${config.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ConciergeBell className="w-5 h-5 text-sky-600" />
              Bot Recepcionista
            </DialogTitle>
            <DialogDescription>
              El bot saluda según la hora ("Buenos días/tardes/noches"), pregunta la necesidad, la IA identifica el tema
              y hace las preguntas del tema antes de pasar al ejecutivo con el resumen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Pregunta inicial (después del saludo automático)</Label>
              <Textarea value={form.pregunta_inicial} onChange={(e) => setForm({ ...form, pregunta_inicial: e.target.value })} rows={2} data-testid="recep-pregunta-inicial" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Temas de atención</Label>
                <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, temas: [...f.temas, { ...TEMA_VACIO }] }))} data-testid="recep-agregar-tema">
                  <Plus className="w-4 h-4 mr-1" />Agregar tema
                </Button>
              </div>
              {form.temas.length === 0 && <p className="text-sm text-slate-500">Agregue los temas que la IA usará para clasificar la necesidad (ej. Pagos de póliza, Reclamos, Cotizaciones...).</p>}
              {form.temas.map((t, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2 bg-slate-50 dark:bg-slate-800/50" data-testid={`recep-tema-${idx}`}>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Nombre del tema (ej. Pagos de póliza)" value={t.nombre} onChange={(e) => setTema(idx, { nombre: e.target.value })} data-testid={`recep-tema-nombre-${idx}`} />
                    <Button variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={() => setForm({ ...form, temas: form.temas.filter((_, i) => i !== idx) })} data-testid={`recep-tema-quitar-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input placeholder="Descripción para la IA (ej. cliente quiere pagar, saldar o abonar a su póliza)" value={t.descripcion || ""} onChange={(e) => setTema(idx, { descripcion: e.target.value })} data-testid={`recep-tema-desc-${idx}`} />
                  <Select value={t.department_id || ""} onValueChange={(v) => setTema(idx, { department_id: v, department_name: deptName(v), assigned_user_id: "", assigned_user_name: "" })}>
                    <SelectTrigger data-testid={`recep-tema-depto-${idx}`}><SelectValue placeholder="Redirigir al departamento..." /></SelectTrigger>
                    <SelectContent>
                      {departamentos.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {t.department_id && (
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-slate-400 shrink-0" />
                      <Select value={t.assigned_user_id || "default"} onValueChange={(v) => {
                        if (v === "default") { setTema(idx, { assigned_user_id: "", assigned_user_name: "" }); }
                        else { const u = usuarios.find((x) => x.id === v); setTema(idx, { assigned_user_id: v, assigned_user_name: u ? userName(u) : "" }); }
                      }}>
                        <SelectTrigger data-testid={`recep-tema-ejecutivo-${idx}`}><SelectValue placeholder="Ejecutivo..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Ejecutivo por defecto del departamento</SelectItem>
                          {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1 pt-1 border-t border-dashed">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-500">📝 Preguntas del tema — el bot las hará una por una</span>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-sky-700" onClick={() => setTema(idx, { preguntas: [...(t.preguntas || []), ""] })} data-testid={`recep-tema-agregar-pregunta-${idx}`}>
                        <Plus className="w-3 h-3 mr-1" />Pregunta
                      </Button>
                    </div>
                    {(t.preguntas || []).map((pq, pi) => (
                      <div key={pi} className="flex items-center gap-1">
                        <span className="text-xs text-slate-400 shrink-0 w-4">{pi + 1}.</span>
                        <Input className="h-8 text-sm" placeholder="Ej: ¿Cuál es el número de póliza?" value={pq}
                          onChange={(e) => setTema(idx, { preguntas: t.preguntas.map((x, j) => (j === pi ? e.target.value : x)) })}
                          data-testid={`recep-tema-pregunta-${idx}-${pi}`} />
                        <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0 shrink-0" onClick={() => setTema(idx, { preguntas: t.preguntas.filter((_, j) => j !== pi) })} data-testid={`recep-tema-quitar-pregunta-${idx}-${pi}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reintentos por pregunta</Label>
                <Input type="number" min={1} max={5} value={form.max_reintentos} onChange={(e) => setForm({ ...form, max_reintentos: e.target.value })} data-testid="recep-max-reintentos" />
                <p className="text-xs text-slate-500 mt-1">Si no responde, se pasa al ejecutivo con lo que se tenga</p>
              </div>
              <div>
                <Label>Reactivar tras inactividad (horas)</Label>
                <Input type="number" min={0} value={form.reactivar_horas} onChange={(e) => setForm({ ...form, reactivar_horas: e.target.value })} data-testid="recep-reactivar-horas" />
                <p className="text-xs text-slate-500 mt-1">0 = solo en conversaciones nuevas</p>
              </div>
            </div>

            <div>
              <Label>Mensaje al pasar al ejecutivo <span className="text-xs text-slate-400">(use {"{departamento}"})</span></Label>
              <Input value={form.mensaje_despedida} onChange={(e) => setForm({ ...form, mensaje_despedida: e.target.value })} data-testid="recep-mensaje-despedida" />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="recep-form-activo" />
              <Label>Recepcionista activo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleGuardar} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="recep-guardar-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar Recepcionista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!statsConfig} onOpenChange={(v) => { if (!v) setStatsConfig(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-sky-600" />
              Estadísticas del Recepcionista — {statsConfig?.name || statsConfig?.phone_number}
            </DialogTitle>
            <DialogDescription>Clientes atendidos por el bot, temas más pedidos y completitud de preguntas</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-sm">Período:</Label>
            <Select value={String(statsDays)} onValueChange={(v) => { setStatsDays(parseInt(v, 10)); if (statsConfig) fetchStats(statsConfig.id, parseInt(v, 10)); }}>
              <SelectTrigger className="w-36 h-8" data-testid="recep-stats-dias"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loadingStats ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : statsData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-sky-600" data-testid="recep-stats-atendidos">{statsData.clientes_atendidos}</p>
                  <p className="text-xs text-slate-500">Clientes atendidos por el bot</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600" data-testid="recep-stats-completaron">{statsData.completaron_preguntas}</p>
                  <p className="text-xs text-slate-500">Completaron las preguntas</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-amber-600" data-testid="recep-stats-no-completaron">{statsData.no_completaron}</p>
                  <p className="text-xs text-slate-500">No completaron (pasaron igual)</p>
                </CardContent></Card>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-xl font-bold text-slate-600" data-testid="recep-stats-directos">{statsData.ruteos_directos}</p>
                  <p className="text-xs text-slate-500">Temas sin preguntas (directo)</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-xl font-bold text-slate-600" data-testid="recep-stats-sin-tema">{statsData.sin_tema}</p>
                  <p className="text-xs text-slate-500">Sin tema identificado (a agente)</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-xl font-bold text-slate-600" data-testid="recep-stats-sin-necesidad">{statsData.sin_necesidad}</p>
                  <p className="text-xs text-slate-500">No indicaron necesidad</p>
                </CardContent></Card>
              </div>
              <div>
                <p className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-200">🏷️ Temas más pedidos</p>
                {statsData.temas.length === 0 ? (
                  <p className="text-sm text-slate-500">Sin datos en este período</p>
                ) : (
                  <div className="space-y-2" data-testid="recep-stats-temas">
                    {statsData.temas.map((t, i) => {
                      const max = statsData.temas[0]?.conteo || 1;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-sm w-48 truncate">{t.nombre}</span>
                          <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-4 overflow-hidden">
                            <div className="bg-sky-500 h-4 rounded-full" style={{ width: `${Math.max(8, (t.conteo / max) * 100)}%` }} />
                          </div>
                          <span className="text-sm font-semibold w-8 text-right">{t.conteo}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4 text-center">Sin datos</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppRecepcionistaSection;
